/**
 * Push-token registration — what the mobile client posts to
 * `POST /api/push-tokens/register` (on app launch / token refresh) and
 * `POST /api/push-tokens/unregister` (on logout) so the backend can deliver
 * Expo push notifications to the device.
 *
 * Both routes are mobile-only. The schema is additive — every field mobile
 * already sends (snake_case) is preserved.
 *
 * Why the platform enum lives here rather than at a wider scope: the route
 * itself currently inline-validates `"ios" | "android"` and returns
 * `invalid_platform` on miss. Lifting the check into the boundary schema is
 * the only behaviour change — the enum value set is identical.
 *
 * @see app/api/push-tokens/register/route.ts — register handler
 * @see app/api/push-tokens/unregister/route.ts — unregister handler
 * @see apps/mobile/lib/push.ts — mobile callers (registerPushToken / unregisterPushToken)
 * @see .tondocs/payments/API_REQUEST_BOUNDARY_AUDIT.md H-12
 */

import { z } from "zod";
import { pinned } from "./pin";

export const PUSH_TOKEN_PLATFORMS = ["ios", "android"] as const;
export type PushTokenPlatform = (typeof PUSH_TOKEN_PLATFORMS)[number];

// ---------------------------------------------------------------------------
// Register
// ---------------------------------------------------------------------------

export interface PushTokenRegisterRequest {
  /** Expo push token string (e.g. `ExponentPushToken[...]`). */
  expo_push_token: string;
  /** Device platform. */
  platform: PushTokenPlatform;
  /**
   * Stable device identifier — `Device.osBuildId` / `Device.modelId`. Optional;
   * persisted on the row for support / debugging.
   */
  device_id?: string | null;
  /** BCP-47 locale string (e.g. "en-US"). Optional. */
  locale?: string | null;
  /** Native app version (e.g. "1.4.2"). Optional. */
  app_version?: string | null;
}

export const PushTokenRegisterRequestSchema = pinned<PushTokenRegisterRequest>()(
  z.object({
    expo_push_token: z.string().trim().min(1),
    platform: z.enum(PUSH_TOKEN_PLATFORMS),
    device_id: z.string().nullable().optional(),
    locale: z.string().nullable().optional(),
    app_version: z.string().nullable().optional(),
  }),
);

// ---------------------------------------------------------------------------
// Unregister
// ---------------------------------------------------------------------------

export interface PushTokenUnregisterRequest {
  /** Expo push token string to forget. */
  expo_push_token: string;
}

export const PushTokenUnregisterRequestSchema = pinned<PushTokenUnregisterRequest>()(
  z.object({
    expo_push_token: z.string().trim().min(1),
  }),
);
